import moment from "moment";

/**
 * Formats a given date into a specified format.
 *
 * @param {any} date - The date to be formatted. This can be a Date object, string, or any value that Moment.js can parse.
 * @param {any} dateFormat - The format string to use for formatting the date. This should be a valid Moment.js format string.
 * @returns {string} - The formatted date as a string.
 *
 * @example
 * // Format a date object
 * const formattedDate = formatDate(new Date(), 'MMMM D, YYYY');
 * console.log(formattedDate); // Output: "July 13, 2024"
 *
 * @example
 * // Format a date string
 * const formattedDate = formatDate('2024-07-13', 'MMMM D, YYYY');
 * console.log(formattedDate); // Output: "July 13, 2024"
 */
export const formatDate = (date: any, dateFromat: any) => {
  return moment(date).format(dateFromat);
};

/**
 * Formats a given Unix timestamp into a specified date format.
 *
 * @param {any} timestamp - The Unix timestamp to be formatted. This should be a value that Moment.js can parse as a Unix timestamp.
 * @param {any} dateFormat - The format string to use for formatting the timestamp. This should be a valid Moment.js format string.
 * @returns {string} - The formatted date as a string.
 *
 * @example
 * // Format a Unix timestamp
 * const formattedDate = formatTimeStamp(1721145600, 'MMMM D, YYYY');
 * console.log(formattedDate); // Output: "July 13, 2024"
 *
 * @example
 * // Format a Unix timestamp with a different format
 * const formattedDate = formatTimeStamp(1721145600, 'YYYY-MM-DD');
 * console.log(formattedDate); // Output: "2024-07-13"
 */
export const formatTimeStamp = (timestamp: any, dateFromat: any) => {
  return moment.unix(timestamp).format(dateFromat);
};

export const formatDateTime = (timestamp: any, dateFromat: any) => {
  return moment(timestamp).format(dateFromat);
};

export const formatDateToISO = (date: Date) => {
  return moment(date).format("YYYY-MM-DD");
};

export const formatToShortDate = (dateString: string) => {
  return moment(new Date(dateString)).format("YYYY-MM-DD");
};

export function formatLongDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatTimeWithAmPm(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatYear(dateStr: string): string {
  const date = new Date(dateStr);
  return date.getFullYear().toString();
}

export const formatSecondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const hStr = hours > 0 ? `${hours}h` : "";
  const mStr = minutes > 0 ? `${minutes}min` : "";

  const result = [hStr, mStr].filter(Boolean).join(" ");
  return result || "0min";
};

export const formatDurationSmart = (seconds: number): string => {
  if (
    !seconds ||
    seconds < 0 ||
    !moment.isDuration(moment.duration(seconds, "seconds"))
  ) {
    return "0:00";
  }

  const duration = moment.duration(Math.floor(seconds), "seconds");
  const hrs = Math.floor(duration.asHours());
  const mins = duration.minutes();
  const secs = duration.seconds();

  if (hrs >= 1) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Formats duration in seconds to MM:SS format
 *
 * @param {number} seconds - The duration in seconds
 * @returns {string} - The formatted duration as MM:SS string
 *
 * @example
 * // Format 125 seconds
 * const formattedDuration = formatDuration(125);
 * console.log(formattedDuration); // Output: "02:05"
 *
 * @example
 * // Format 65 seconds
 * const formattedDuration = formatDuration(65);
 * console.log(formattedDuration); // Output: "01:05"
 */
export const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes?.toString()?.padStart(2, "0")}:${remainingSeconds
    ?.toString()
    ?.padStart(2, "0")}`;
};

/**
 * Generates a summary string for a chapter with video count and total duration
 *
 * @param {any} chapter - The chapter object containing courseVideos array
 * @returns {string} - The formatted chapter summary (e.g., "3 lectures • 1hr 15min")
 *
 * @example
 * // Chapter with 3 videos totaling 75 minutes
 * const chapter = { courseVideos: [{ duration: 25 }, { duration: 30 }, { duration: 20 }] };
 * const summary = getChapterSummary(chapter);
 * console.log(summary); // Output: "3 lectures • 1hr 15min"
 *
 * @example
 * // Chapter with 1 video totaling 45 minutes
 * const chapter = { courseVideos: [{ duration: 2700 }] };
 * const summary = getChapterSummary(chapter);
 * console.log(summary); // Output: "1 lecture • 45min"
 */
export const getChapterSummary = (chapter: any): string => {
  const videoCount = chapter?.courseVideos?.length || 0;
  const totalDuration =
    chapter?.courseVideos?.reduce(
      (acc: number, video: any) => acc + (video?.duration || 0),
      0
    ) || 0;
  const hours = Math.floor(totalDuration / 3600);
  const minutes = Math.floor((totalDuration % 3600) / 60);
  const seconds = totalDuration % 60;
  const timeStr = hours > 0
    ? `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${minutes}:${seconds.toString().padStart(2, "0")}`;
  return `${videoCount} lecture${videoCount !== 1 ? "s" : ""} • ${timeStr}`;
};

/**
 * Calculates and returns a human-readable time ago string from a given date.
 *
 * @param {string | undefined} dateString - The date string to calculate time ago from
 * @returns {string} - A formatted string like "5 minutes ago", "2 hours ago", "3 days ago", etc., or "-" if no date provided
 *
 * @example
 * // Get time ago for a date 5 minutes in the past
 * const timeAgo = getTimeAgo("2024-01-15T10:00:00Z");
 * console.log(timeAgo); // Output: "5 minutes ago"
 *
 * @example
 * // Handle undefined date
 * const timeAgo = getTimeAgo(undefined);
 * console.log(timeAgo); // Output: "-"
 */
export const getTimeAgo = (dateString?: string): string => {
  if (!dateString) return "-";
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec} seconds ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12)
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;
  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear > 1 ? "s" : ""} ago`;
};